import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { Restaurant, TransactionFormData, UploadUrlResponse } from '../types';
import './Pages.css';

const SubmitPoints: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [formData, setFormData] = useState<TransactionFormData>({
    restaurantId: '',
    amount: '',
    transactionDate: new Date().toISOString().split('T')[0],
    receiptPhotoUrl: '',
    totalParticipants: 1,
    isSplitBill: false,
    totalAmount: '',
    paidAmount: ''
  });
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);

  useEffect(() => {
    loadRestaurants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const restaurantId = searchParams.get('restaurantId');
    if (restaurantId && restaurants.length > 0) {
      setFormData(prev => ({ ...prev, restaurantId }));
      const restaurant = restaurants.find(r => r.restaurantId === restaurantId);
      if (restaurant) {
        setSelectedRestaurant(restaurant);
      }
    }
  }, [searchParams, restaurants]);

  useEffect(() => {
    const restaurant = restaurants.find(r => r.restaurantId === formData.restaurantId);
    setSelectedRestaurant(restaurant || null);
  }, [formData.restaurantId, restaurants]);

  const loadRestaurants = async () => {
    try {
      const response = await api.get<Restaurant[]>('/restaurants');
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadReceipt = async (): Promise<string | null> => {
    if (!selectedFile) return null;

    try {
      setUploading(true);
      
      // Upload file directly to API
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post<UploadUrlResponse>('/client/receipts/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.fileUrl;
    } catch (error) {
      console.error('Error uploading receipt:', error);
      alert('Erro ao fazer upload da foto');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate receipt photo is required
    if (!selectedFile) {
      alert('Por favor, selecione uma foto da nota fiscal');
      return;
    }

    try {
      // Upload receipt photo
      const photoUrl = await uploadReceipt();
      
      if (!photoUrl) {
        alert('Erro ao fazer upload da foto. Tente novamente.');
        return;
      }

      // Prepare transaction data
      const transactionData: any = {
        restaurantId: formData.restaurantId,
        transactionDate: formData.transactionDate,
        receiptPhotoUrl: photoUrl,
        totalParticipants: parseInt(formData.totalParticipants.toString()) || 1
      };

      // If split bill, use totalAmount and paidAmount
      if (formData.isSplitBill) {
        const totalAmount = parseFloat(formData.totalAmount);
        const paidAmount = parseFloat(formData.paidAmount);
        
        if (!totalAmount || totalAmount <= 0) {
          alert('Por favor, informe o valor total da nota fiscal');
          return;
        }
        
        if (!paidAmount || paidAmount <= 0) {
          alert('Por favor, informe o valor que você pagou');
          return;
        }
        
        if (paidAmount > totalAmount) {
          alert('O valor pago não pode ser maior que o valor total da nota fiscal');
          return;
        }
        
        transactionData.amount = totalAmount;
        transactionData.totalAmount = totalAmount;
        transactionData.paidAmount = paidAmount;
      } else {
        const amount = parseFloat(formData.amount);
        if (!amount || amount <= 0) {
          alert('Por favor, informe o valor da compra');
          return;
        }
        transactionData.amount = amount;
      }

      // Submit transaction
      await api.post('/client/transactions', transactionData);

      alert('Pontos cadastrados com sucesso! Aguarde a aprovação do restaurante.');
      setFormData({
        restaurantId: '',
        amount: '',
        transactionDate: new Date().toISOString().split('T')[0],
        receiptPhotoUrl: '',
        totalParticipants: 1,
        isSplitBill: false,
        totalAmount: '',
        paidAmount: ''
      });
      setSelectedFile(null);
      setSelectedRestaurant(null);
    } catch (error) {
      console.error('Error submitting transaction:', error);
      alert('Erro ao cadastrar pontos');
    }
  };

  return (
    <div className="page">
      <div className="container">
        <h1>Cadastrar Pontos</h1>
        <p className="subtitle">Envie sua nota fiscal para acumular pontos</p>

        <div className="form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Restaurante *</label>
              <select
                required
                value={formData.restaurantId}
                onChange={(e) => setFormData({...formData, restaurantId: e.target.value})}
              >
                <option value="">Selecione um restaurante</option>
                {restaurants.map((restaurant) => (
                  <option key={restaurant.restaurantId} value={restaurant.restaurantId}>
                    {restaurant.name}
                  </option>
                ))}
              </select>
              {selectedRestaurant && (
                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '14px' }}>
                  <strong>Estabelecimento selecionado:</strong> {selectedRestaurant.name}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Data da Compra *</label>
                <input
                  type="date"
                  required
                  value={formData.transactionDate}
                  onChange={(e) => setFormData({...formData, transactionDate: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Valor (R$) *</label>
                <input
                  type="number"
                  required={!formData.isSplitBill}
                  disabled={formData.isSplitBill}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                />
                {formData.isSplitBill && (
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '4px' }}>
                    Use os campos abaixo quando a conta for dividida
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Foto da Nota Fiscal *</label>
              <div className="file-input">
                <input
                  type="file"
                  accept="image/*"
                  required
                  onChange={handleFileSelect}
                />
                {selectedFile && <span className="file-name">📷 {selectedFile.name}</span>}
                {!selectedFile && (
                  <span style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    ⚠️ Foto da nota fiscal é obrigatória
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isSplitBill}
                  onChange={(e) => setFormData({...formData, isSplitBill: e.target.checked, totalAmount: '', paidAmount: '', amount: ''})}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Está dividindo sua conta?</span>
              </label>
            </div>

            {formData.isSplitBill && (
              <div style={{ 
                marginTop: '16px', 
                padding: '16px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Valor Total da Nota Fiscal (R$) *</label>
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.totalAmount}
                      onChange={(e) => setFormData({...formData, totalAmount: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label>Valor que Você Pagou (R$) *</label>
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.paidAmount}
                      onChange={(e) => setFormData({...formData, paidAmount: e.target.value})}
                    />
                  </div>
                </div>
                <div className="info-box" style={{ marginTop: '12px', marginBottom: '0' }}>
                  💡 <strong>Dica:</strong> Informe o valor total da nota fiscal e quanto você pagou. 
                  Os pontos serão calculados apenas sobre o valor que você pagou!
                </div>
              </div>
            )}

            <button
              type="submit"
              className="btn-submit-form"
              disabled={uploading}
            >
              {uploading ? 'Enviando...' : '✓ Enviar para Aprovação'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmitPoints;

